var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
import i18n from "../../i18n";
import { API_BASE_URL } from "../../config/app-config";
import { clearAppToken, exchangeAppToken, getAppToken, } from "../../auth/appToken";
const mutex = new Mutex();
const rawBaseQuery = fetchBaseQuery({ baseUrl: API_BASE_URL });
function toFetchArgs(args) {
    if (typeof args === "string")
        return { url: args };
    return args;
}
function withHeaders(args, token) {
    const next = toFetchArgs(args);
    const headers = new Headers(next.headers || {});
    headers.set("Accept-Language", i18n.language || "en");
    headers.set("Authorization", `Bearer ${token}`);
    return Object.assign(Object.assign({}, next), { headers });
}
// 403 here means: authenticated via SSO, but not a provisioned app user. We surface it as a
// 403 on the request so PermissionContext shows the "not authorized" page (instead of a
// login loop). "failed" surfaces as 401.
const forbiddenError = {
    status: 403,
    data: "not-authorized-for-app",
};
const authFailedError = {
    status: 401,
    data: "auth-failed",
};
function errorFor(result) {
    return result.status === "forbidden" ? forbiddenError : authFailedError;
}
export const baseQueryWithMsal = (args, api, extraOptions) => __awaiter(void 0, void 0, void 0, function* () {
    yield mutex.waitForUnlock();
    // Ensure we have an app JWT (lazily exchange the Azure token on first use).
    let token = getAppToken();
    if (!token) {
        const exchanged = yield exchangeAppToken();
        if (exchanged.status !== "ok")
            return { error: errorFor(exchanged) };
        token = exchanged.token;
    }
    let result = yield rawBaseQuery(withHeaders(args, token), api, extraOptions);
    // App JWT likely expired → re-exchange once and retry.
    if (result.error && result.error.status === 401) {
        if (!mutex.isLocked()) {
            const release = yield mutex.acquire();
            try {
                clearAppToken();
                const exchanged = yield exchangeAppToken();
                if (exchanged.status === "ok") {
                    result = yield rawBaseQuery(withHeaders(args, exchanged.token), api, extraOptions);
                }
                else {
                    result = { error: errorFor(exchanged) };
                }
            }
            finally {
                release();
            }
        }
        else {
            // Another call is already refreshing — wait, then retry with the fresh token.
            yield mutex.waitForUnlock();
            const fresh = getAppToken();
            if (fresh) {
                result = yield rawBaseQuery(withHeaders(args, fresh), api, extraOptions);
            }
        }
    }
    return result;
});
